import { Theme, createTheme } from '@mui/material/styles';
import { TenantDisplayPalette } from '@askporter/client-grieg-lyric';
import { customPalette, customTypography, breakpoints, customComponents } from './overrides';
/*
  have to use module augmentation to add new variables to the Theme and ThemeOptions. This way we can have custom spacing, etc.
  official guide: https://mui.com/material-ui/customization/theming/#custom-variables
  stackoverflow example: https://stackoverflow.com/questions/58168798/add-custom-theme-variable-in-createtheme
*/
declare module '@mui/material/styles' {
  interface CustomTheme {
    custom: {
      spacing?: {
        xxs: number;
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
        '2xl': number;
        '3xl': number;
        '4xl': number;
        '5xl': number;
        '6xl': number;
        '7xl': number;
        '8xl': number;
        '9xl': number;
        '10xl': number;
        '11xl': number;
      };
      radius?: {
        xxs: number;
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
        '2xl': number;
        '3xl': number;
        '4xl': number;
        full: number;
      };
      width?: {
        xxs: number;
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
        '2xl': number;
        '3xl': number;
        '4xl': number;
        '5xl': number;
        '6xl': number;
      };
      colours?: {
        // Grey
        grey25: string;
        grey50: string;
        grey100: string;
        grey200: string;
        grey300: string;
        grey400: string;
        grey500: string;
        grey600: string;
        grey700: string;
        grey800: string;
        grey900: string;
        grey950: string;
        // Error
        error25: string;
        error50: string;
        error100: string;
        error200: string;
        error300: string;
        error400: string;
        error500: string;
        error600: string;
        error700: string;
        error800: string;
        error900: string;
        error950: string;
        // Warning
        warning25: string;
        warning50: string;
        warning100: string;
        warning200: string;
        warning300: string;
        warning400: string;
        warning500: string;
        warning600: string;
        warning700: string;
        warning800: string;
        warning900: string;
        warning950: string;
        // Success
        success25: string;
        success50: string;
        success100: string;
        success200: string;
        success300: string;
        success400: string;
        success500: string;
        success600: string;
        success700: string;
        success800: string;
        success900: string;
        success950: string;
      };
    };
  }
  interface Palette extends PaletteVariants {}
  interface PaletteOptions extends PaletteVariantsOptions {}
  interface Theme extends CustomTheme {}
  interface ThemeOptions extends CustomTheme {}
}
/**
 * Creates the Material UI theme object with our design tokens
 *
 * @param configPalette - the tenant's colours conforming to the ConfigPalette type
 *
 * @returns Theme - the MUI Theme object
 */
export const createAPTheme = (cp: Required<TenantDisplayPalette>): Theme => {
  return createTheme({
    components: customComponents(cp).components,
    breakpoints,
    spacing: (factor: number) => `${0.25 * factor}rem`,
    typography: customTypography.typography,
    palette: customPalette(cp),
    custom: {
      spacing: {
        xxs: 0.5, // 0.125rem, 2px
        xs: 1, // 0.25rem, 4px
        sm: 1.5, // 0.375rem, 6px
        md: 2, // 0.5rem, 8px
        lg: 3, // 0.75rem, 12px
        xl: 4, // 1rem, 16px
        '2xl': 5, // 1.25rem, 20px
        '3xl': 6, // 1.5rem, 24px
        '4xl': 8, // 2rem, 32px
        '5xl': 10, // 2.5rem, 40px
        '6xl': 12, // 3rem, 48px
        '7xl': 16, // 4rem, 64px
        '8xl': 20, // 5rem, 80px
        '9xl': 24, // 6rem, 96px
        '10xl': 32, // 8rem, 128px
        '11xl': 40, // 10rem, 160px
      },
      radius: {
        xxs: 0.5,
        xs: 1,
        sm: 1.5,
        md: 2,
        lg: 2.5,
        xl: 3,
        '2xl': 4,
        '3xl': 5,
        '4xl': 6,
        full: 9999,
      },
      width: {
        xxs: 60, // 20rem, 320px
        xs: 72, // 24rem, 384px
        sm: 90, // 30rem, 480px
        md: 105, // 35rem, 560px
        lg: 120, // 40rem, 640px
        xl: 144, // 48rem, 768px
        '2xl': 192, // 64rem, 1024px
        '3xl': 240, // 80rem, 1280px
        '4xl': 270, // 90rem, 1440px
        '5xl': 300, // 100rem, 1600px
        '6xl': 360, // 120rem, 1920px
      },
      colours: {
        // Grey
        grey25: '#FCFCFD',
        grey50: '#F9FAFB',
        grey100: '#F2F4F7',
        grey200: '#EAECF0',
        grey300: '#D0D5DD',
        grey400: '#98A2B3',
        grey500: '#667085',
        grey600: '#475467',
        grey700: '#344054',
        grey800: '#182230',
        grey900: '#101828',
        grey950: '#0C111D',
        // Error
        error25: '#FFFBFA',
        error50: '#FEF3F2',
        error100: '#FEE4E2',
        error200: '#FECDCA',
        error300: '#FDA29B',
        error400: '#F97066',
        error500: '#F04438',
        error600: '#D92D20',
        error700: '#B42318',
        error800: '#912018',
        error900: '#7A271A',
        error950: '#55160C',
        // Warning
        warning25: '#FFFCF5',
        warning50: '#FFFAEB',
        warning100: '#FEF0C7',
        warning200: '#FEDF89',
        warning300: '#FEC84B',
        warning400: '#FDB022',
        warning500: '#F79009',
        warning600: '#DC6803',
        warning700: '#B54708',
        warning800: '#93370D',
        warning900: '#7A2E0E',
        warning950: '#4E1D09',
        // Success
        success25: '#F6FEF9',
        success50: '#ECFDF3',
        success100: '#DCFAE6',
        success200: '#ABEFC6',
        success300: '#75E0A7',
        success400: '#47CD89',
        success500: '#17B26A',
        success600: '#079455',
        success700: '#067647',
        success800: '#085D3A',
        success900: '#074D31',
        success950: '#053321',
      },
    },
  });
};
export { fallbackTheme } from './fallbackTheme';
export { fallbackPalette } from './fallbackPalette';
